/**
 * The back arrow beside a page title.
 *
 * Optional, not the only way out — the browser's own back button still works,
 * and this exists so nobody has to reach for it. Anyone who arrived by
 * following a search result should be able to get back to that result without
 * leaving the page they are reading to go hunting.
 *
 * IT IS A REAL LINK, not a button calling history.back(). That matters for
 * three things a button loses: cmd-click and middle-click open the destination
 * in a new tab, the status bar shows where it goes on hover, and a screen
 * reader announces it as a link with a destination rather than as an unlabelled
 * control. The click handler then upgrades the behaviour to history when there
 * is history to use.
 *
 * WHY NOT ALWAYS navigate(-1): a policy link arrives by email. Somebody opens
 * it, lands on the detail page as the first entry in that tab's history, and
 * presses back — which takes them out of the application entirely, to their
 * inbox. So history is used only when this app put something behind us;
 * otherwise the link goes where it says it goes.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './BackLink.module.css';

export interface BackLinkProps {
  /**
   * Where to go when there is no history to return to. Also the link's real
   * href, so hover and cmd-click tell the truth.
   */
  to: string;
  /**
   * What the destination is called, for the accessible name: "Back to
   * policies". Never rendered as text — the arrow carries it visually, and a
   * word beside every title would crowd the thing people came to read.
   */
  label: string;
  /**
   * Always go to `to`, never through browser history. For a back arrow
   * INSIDE one page that moves through steps: there the arrow means "the
   * start of this", and history may hold something else entirely (the
   * sidebar link that led here, a step that pushed an entry).
   */
  direct?: boolean;
}

/**
 * Whether React Router has a previous entry of its own.
 *
 * `location.key` is 'default' only for the entry the router started on, which
 * is exactly the case where going back leaves the app.
 */
export function canGoBack(locationKey: string, historyLength: number): boolean {
  return locationKey !== 'default' && historyLength > 1;
}

export function BackLink({ to, label, direct = false }: BackLinkProps) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Link
      to={to}
      className={styles.back}
      aria-label={label}
      title={label}
      onClick={(e) => {
        // Leave the browser to handle anything that means "open elsewhere":
        // a modifier click, or a middle click arriving as an auxiliary button.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        if (direct) return; // the link's own href is the destination
        if (!canGoBack(location.key, window.history.length)) return;

        e.preventDefault();
        navigate(-1);
      }}
    >
      <svg width="17" height="17" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <path
          d="M12.5 4.5L7 10l5.5 5.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Link>
  );
}
